import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, InputBase, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import supabase from "../../supabase";
import { tokens } from "../../theme";
import { getInitials } from "../../utils/initials";
import { IconClose, IconPlus, IconSearch } from "../ui/icons";
import { LoaderSpinner } from "../spinner/loader";

/**
 * Paso 1: elegir el cliente sin salir de la pantalla.
 * Antes esto vivía en un modal con una tabla completa; en la calle, con una
 * mano, buscar y tocar una fila es más rápido.
 */
const ClientPicker = ({ formState, setFormState, isReadOnlyMode }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("clients")
      .select("*")
      .order("company", { ascending: true });

    if (error) {
      console.error("Error cargando clientes:", error);
      setClients([]);
    } else {
      setClients(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectedId = formState.client?.clientId;

  const visible = useMemo(() => {
    const query = term.trim().toLowerCase();
    if (!query) return clients.slice(0, 40);
    return clients
      .filter((client) =>
        [client.company, client.name, client.lastName, client.cuit, client.address]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query))
      )
      .slice(0, 40);
  }, [clients, term]);

  const choose = (client) => {
    if (isReadOnlyMode) return;
    setFormState((prev) => ({ ...prev, client }));
  };

  return (
    <Box
      className="step_container"
      sx={{ display: "flex", flexDirection: "column", gap: "14px", width: "100%" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "9px",
          flexGrow: 1,
          minWidth: { xs: "100%", sm: 260 },
          minHeight: { xs: 46, md: 44 },
          padding: "0 13px",
          border: `1px solid ${tokens.border}`,
          borderRadius: "10px",
          backgroundColor: tokens.surface,
          "&:focus-within": {
            borderColor: tokens.brand,
            boxShadow: `0 0 0 3px ${tokens.brandTint}`,
          },
        }}
      >
        <Box sx={{ color: tokens.ink3, display: "flex" }}>
          <IconSearch size={17} />
        </Box>
        <InputBase
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Buscar por empresa, CUIT o domicilio…"
          sx={{ flexGrow: 1, fontSize: { xs: "15px", md: "14.5px" } }}
          inputProps={{ "aria-label": "Buscar cliente" }}
        />
        {term && (
          <Box
            component="button"
            type="button"
            onClick={() => setTerm("")}
            aria-label="Borrar búsqueda"
            sx={{
              display: "flex",
              color: tokens.ink3,
              cursor: "pointer",
              border: "none",
              padding: 0,
              background: "transparent",
            }}
          >
            <IconClose size={15} />
          </Box>
        )}
      </Box>

      {/* Junto al buscador: antes vivía al final de la lista y había que scrollear. */}
      {!isReadOnlyMode && (
        <Button
          variant="outlined"
          startIcon={<IconPlus size={16} />}
          onClick={() => navigate("/edit-client", { state: { returnTo: "/new-order" } })}
          sx={{
            minHeight: { xs: 46, md: 44 },
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
            whiteSpace: "nowrap",
          }}
        >
          Cliente nuevo por CUIT
        </Button>
      )}
      </Box>

      <Typography
        sx={{
          fontSize: "11.5px",
          fontWeight: 600,
          color: tokens.ink3,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {term
          ? `${visible.length} ${visible.length === 1 ? "coincidencia" : "coincidencias"}`
          : "Clientes"}
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
          <LoaderSpinner />
        </Box>
      ) : (
        <Box
          sx={{
            border: `1px solid ${tokens.border}`,
            borderRadius: `${tokens.radiusCard}px`,
            backgroundColor: tokens.surface,
            overflow: "hidden",
            // Altura acotada también en teléfono: antes había que recorrer toda
            // la lista para llegar al botón Siguiente.
            maxHeight: { xs: "58vh", md: 460 },
            overflowY: "auto",
          }}
        >
          {visible.length === 0 ? (
            <Box sx={{ padding: "28px 16px", textAlign: "center" }}>
              <Typography sx={{ fontSize: "13.5px", color: tokens.ink3 }}>
                No se encontró ningún cliente con ese dato.
              </Typography>
            </Box>
          ) : (
            visible.map((client, index) => {
              const selected = client.clientId === selectedId;
              return (
                <Box
                  key={client.clientId}
                  onClick={() => choose(client)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      choose(client);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 14px",
                    minHeight: 68,
                    cursor: isReadOnlyMode ? "default" : "pointer",
                    borderBottom:
                      index === visible.length - 1 ? "none" : "1px solid #f5f3f1",
                    backgroundColor: selected ? tokens.brandTint2 : "transparent",
                    boxShadow: selected ? `inset 3px 0 0 ${tokens.brand}` : "none",
                    "&:hover": { backgroundColor: selected ? tokens.brandTint2 : tokens.surface2 },
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      flexShrink: 0,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "13px",
                      fontWeight: 600,
                      backgroundColor: selected ? tokens.brandTint : tokens.borderSoft,
                      color: selected ? tokens.brandDark : tokens.ink2,
                    }}
                  >
                    {getInitials(client.company || client.name)}
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "14.5px",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {client.company || `${client.name || ""} ${client.lastName || ""}`.trim()}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "11.5px",
                        color: tokens.ink3,
                        fontVariantNumeric: "tabular-nums",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {[client.cuit, client.address].filter(Boolean).join(" · ") ||
                        "Sin CUIT ni domicilio"}
                    </Typography>
                  </Box>

                  {selected && (
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        flexShrink: 0,
                        borderRadius: 999,
                        backgroundColor: tokens.brand,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M5 12.5l4.5 4.5L19 7" />
                      </svg>
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Box>
      )}

    </Box>
  );
};

export default ClientPicker;
